import { useMemo } from 'react';
import { type ComponentProps } from 'react';

import { type Asset } from 'contentful';

import { getCloudinaryUrl, ProductCardConstants } from '@sg/garnish';

import { type ProductDetailsCardCarousel } from '@order/features/product-details';
import { useFeatureFlag } from '@order/LaunchDarkly';

// ─────────────────────────────────────────────────────────────────────────────

export const useProductDetailsCarouselItems = (params: UseProductDetailsCarouselItemsParams) => {
  const { carousel, productAssetUrl, productName } = params;

  const carouselVariation = useFeatureFlag('CELS-4498-product-details-carousel-variation'); // prettier-ignore

  // ─────────────────────────────────────────────────────────────────────────────

  const carouselItems = useMemo<ProductDetailsCardCarouselItems>(() => {
    if (!carousel || carousel.length === 0) return [];

    const { IMAGE_TRANSFORM_CONFIG } = ProductCardConstants;

    const baseImageItem: ProductDetailsCardCarouselItems[number] = {
      type: 'image',
      source: getCloudinaryUrl(productAssetUrl, IMAGE_TRANSFORM_CONFIG),
      alt: productName,
      contentFit: 'contain',
    };
    const otherItems = carousel.map(transformAssetToCarouselItem);

    if (carouselVariation === 'BASE_IMAGE_LAST') {
      return [...otherItems, baseImageItem];
    }

    if (carouselVariation === 'HIDE_BASE_IMAGE') {
      return otherItems;
    }

    return [baseImageItem, ...otherItems];
  }, [carousel, productAssetUrl, productName, carouselVariation]);

  // ───────────────────────────────────────────────────────────────────────────

  return {
    carouselItems,
    carouselVariation,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function transformAssetToCarouselItem(asset: {
  fields: { imageVideo: Asset; staticImage?: Asset };
}): ProductDetailsCardCarouselItems[number] {
  const media = asset.fields.imageVideo;
  const isVideo = media.fields.file.contentType.startsWith('video');
  const source = media.fields.file.url;
  const staticSource = asset.fields.staticImage?.fields?.file?.url;
  const alt = media.fields.title ?? media.fields.description;
  const type = isVideo ? 'video' : 'image';

  return { type, source, staticSource, alt };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type ProductDetailsCardCarouselItems = ComponentProps<typeof ProductDetailsCardCarousel>['items'];

type UseProductDetailsCarouselItemsParams = {
  carousel: { fields: { imageVideo: Asset; staticImage?: Asset } }[] | undefined;
  productAssetUrl: string;
  productName: string;
};
